import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import CircularProgress from "@mui/material/CircularProgress";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import InvoicesApi from "api/invoices";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import html2pdf from "html2pdf.js";
import CompanyApi from "api/companyInfo";
import { useTranslation } from "react-i18next";
import { Typography, Box, useMediaQuery, useTheme } from "@mui/material";
import numberToWords from "utils/numberToWords";
import numberToWordsArabic from "utils/numberToWordsArabic";
import numberToWordsEnglish from "utils/numberToWordEnglish";

function InvoiceDetails() {
  const { id } = useParams();
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [companyInfos, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add theme and media query hooks for responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { t, i18n } = useTranslation();

  const manager = localStorage.getItem("manager");

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        setError(new Error("Token not found."));
        setLoading(false);
        return;
      }

      try {
        const response = await InvoicesApi.GetInvoicesById(token, id);
        setInvoiceDetails(response.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    const fetchCompanyInfos = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        setError(new Error("Token not found."));
        setLoading(false);
        return;
      }

      try {
        const response = await CompanyApi.GetCompanyInfos(token);
        setCompanyInfo(response.data.results[0]);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoiceDetails();
    fetchCompanyInfos();
  }, [id]);

  if (loading)
    return (
      <SoftBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress sx={{ color: "#1F51FF" }} />
      </SoftBox>
    );

  if (error)
    return (
      <SoftBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <SoftTypography variant="body1" color="error">
          {t("Erreur")}: {error.message}
        </SoftTypography>
      </SoftBox>
    );

  // eslint-disable-next-line
  const invoiceDate =
    new Date(invoiceDetails?.date_created).toLocaleDateString() || "N/A";

  const downloadInvoice = (name) => {
    const element = document.getElementById("invoice-content");

    const options = {
      margin: isMobile ? [0.1, 0.1, 0.1, 0.1] : [0.2, 0.2, 0.2, 0.2], // Smaller margins on mobile
      filename: `${name}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      pagebreak: { mode: "avoid-all" },
    };

    // Apply RTL styles if the language is Arabic
    if (i18n.language === "ar") {
      element.style.direction = "rtl";
      element.style.textAlign = "ltr";
    }

    html2pdf().from(element).set(options).save();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftBox>
        <Grid container justifyContent="center">
          <Grid item xs={12} xl={10}>
            <Card
              id="invoice-content"
              style={{
                fontFamily: "sans-serif",
                overflow: isMobile ? "hidden" : "visible",
              }}
            >
              {/* Header with improved responsiveness */}
              <SoftBox px={isMobile ? 2 : 3} mt={3}>
                <Grid
                  container
                  justifyContent="space-between"
                  spacing={isMobile ? 2 : 0}
                >
                  <Grid
                    item
                    xs={12}
                    md={4}
                    display="flex"
                    flexDirection="column"
                    mb={isMobile ? 2 : 0}
                  >
                    <SoftTypography
                      variant={isMobile ? "subtitle1" : "h6"}
                      fontWeight="medium"
                    >
                      {companyInfos?.company_name}
                    </SoftTypography>
                    <SoftBox display="flex" flexDirection="column">
                      <SoftTypography variant="caption" fontWeight="medium">
                        {companyInfos?.address}
                      </SoftTypography>
                      <SoftTypography variant="caption" fontWeight="medium">
                        {t("Téléphone")}: {companyInfos?.contact_phone}
                      </SoftTypography>
                      <SoftTypography variant="caption" fontWeight="medium">
                        {t("Email")}: {companyInfos?.contact_email}
                      </SoftTypography>
                    </SoftBox>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    container
                    display="flex"
                    justifyContent={isMobile ? "flex-start" : "center"}
                    alignItems="center"
                    columnGap={1}
                    mb={isMobile ? 2 : 0}
                  >
                    <SoftTypography variant="subtitle2" fontWeight="medium">
                      {t("Facture")}:
                    </SoftTypography>
                    <SoftTypography variant="subtitle2" fontWeight="medium">
                      {invoiceDetails?.number || ""}
                    </SoftTypography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    display="flex"
                    flexDirection="column"
                    alignItems={isMobile ? "flex-start" : "flex-end"}
                  >
                    <SoftTypography variant="caption" fontWeight="medium">
                      {t("Numéro fiscal")}: {companyInfos?.code_tva}
                    </SoftTypography>
                    <SoftTypography variant="caption" fontWeight="medium">
                      {t("Registre de commerce")}:{" "}
                      {companyInfos?.business_registration_number}
                    </SoftTypography>
                    <SoftTypography variant="caption" fontWeight="medium">
                      {t("Banque")}: {companyInfos?.bank_id_statement}
                    </SoftTypography>
                  </Grid>
                </Grid>
              </SoftBox>

              <SoftBox display="flex" width="100%" px={isMobile ? 2 : 3} pt={3}>
                <SoftBox
                  sx={{
                    width: "100%",
                    borderTop: "2px solid black",
                    borderLeft: "2px solid black",
                    borderRight: "2px solid black",
                    borderBottom: "1px solid black",
                    borderRadius: 2,
                    padding: isMobile ? 1 : 2,
                  }}
                >
                  <Grid container spacing={isMobile ? 1 : 0}>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display="flex"
                      gap={1}
                      mb={isMobile ? 1 : 0}
                    >
                      <Typography
                        fontSize={isMobile ? 13 : 15}
                        fontWeight="medium"
                      >
                        {t("Sous la responsabilité de M.")}
                      </Typography>
                      <Typography
                        fontSize={isMobile ? 13 : 15}
                        fontWeight="medium"
                      >
                        {invoiceDetails?.client}
                      </Typography>
                      <Typography
                        fontSize={isMobile ? 13 : 15}
                        fontWeight="medium"
                      >
                        {invoiceDetails?.orders[0]?.customer}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display="flex"
                      gap={1}
                      justifyContent={isMobile ? "flex-start" : "flex-end"}
                    >
                      <Typography
                        fontSize={isMobile ? 13 : 15}
                        fontWeight="medium"
                      >
                        {t("Utilisateur")}:
                      </Typography>
                      <Typography
                        fontSize={isMobile ? 13 : 15}
                        fontWeight="medium"
                      >
                        {manager}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={isMobile ? 1 : 0}>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display="flex"
                      gap={isMobile ? 1 : 2}
                    >
                      <Typography
                        fontSize={isMobile ? 13 : 15}
                        fontWeight="medium"
                      >
                        {t("Adresse")}:
                      </Typography>
                      <Typography
                        fontSize={isMobile ? 13 : 15}
                        fontWeight="medium"
                        sx={{ wordBreak: "break-word" }}
                      >
                        {invoiceDetails?.address_detail}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display="flex"
                      gap={isMobile ? 1 : 2}
                      justifyContent={isMobile ? "flex-start" : "flex-end"}
                      mt={isMobile ? 1 : 0}
                    >
                      <Typography
                        fontSize={isMobile ? 13 : 15}
                        fontWeight="medium"
                      >
                        {companyInfos?.city}, {t("le")} {invoiceDate}
                      </Typography>
                      <Typography
                        fontSize={isMobile ? 13 : 15}
                        fontWeight="medium"
                      >
                        {t("à")} 13:47
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display="flex"
                      mx={isMobile ? 0 : 12}
                      mt={1}
                      justifyContent={isMobile ? "flex-start" : "center"}
                    >
                      <Typography
                        fontSize={isMobile ? 13 : 15}
                        fontWeight="medium"
                      >
                        {t("Non soumis à l'ajout")}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display="flex"
                      mx={isMobile ? 0 : 12}
                      justifyContent={isMobile ? "flex-start" : "center"}
                    >
                      <Typography
                        fontSize={isMobile ? 13 : 15}
                        fontWeight="medium"
                      >
                        {invoiceDetails?.orders[0]?.mat_fiscale}
                      </Typography>
                    </Grid>
                  </Grid>
                </SoftBox>
              </SoftBox>
              {/* Main content with responsive table */}
              <SoftBox px={isMobile ? 2 : 3} mt={isMobile ? 2 : 0}>
                <Box
                  sx={{
                    border: "2px solid black",
                    borderRadius: 2,
                    width: "100%",
                    overflowX: "auto", // Enable horizontal scrolling for mobile
                    minHeight: "32vh",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Table
                    sx={{
                      minWidth: isMobile ? "100%" : "32rem",
                      borderCollapse: "collapse",
                      flexGrow: 1,
                      height: "100%",
                      tableLayout: isMobile ? "fixed" : "auto", // Fixed table layout on mobile
                    }}
                  >
                    <SoftBox
                      component="thead"
                      sx={{
                        width: "100%",
                        borderBottom: "2px solid black",
                      }}
                    >
                      <TableRow
                        sx={{ width: "100%", padding: isMobile ? 1 : 2 }}
                      >
                        {/* Table Headers */}
                        <SoftBox
                          component="th"
                          py={isMobile ? 0.5 : 1}
                          textAlign="center"
                          sx={{
                            borderBottom: "2px solid black",
                            width: isMobile ? "15%" : "auto",
                            fontSize: isMobile ? "0.7rem" : "inherit",
                          }}
                        >
                          <SoftTypography
                            variant={isMobile ? "caption" : "subtitle2"}
                            fontWeight="medium"
                          >
                            {t("Code Article")}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox
                          component="th"
                          py={isMobile ? 0.5 : 1}
                          textAlign="center"
                          sx={{
                            borderBottom: "2px solid black",
                            borderLeft: "2px solid black",
                            borderRight: "2px solid black",
                            width: isMobile ? "30%" : "auto",
                            fontSize: isMobile ? "0.7rem" : "inherit",
                          }}
                        >
                          <SoftTypography
                            variant={isMobile ? "caption" : "subtitle2"}
                            fontWeight="medium"
                          >
                            {t("Designation")}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox
                          component="th"
                          py={isMobile ? 0.5 : 1}
                          textAlign="center"
                          sx={{
                            borderBottom: "2px solid black",
                            borderRight:
                              i18n.language === "ar" ? 0 : "2px solid black",
                            borderLeft:
                              i18n.language === "ar" ? "2px solid black" : 0,
                            width: isMobile ? "15%" : "auto",
                            fontSize: isMobile ? "0.7rem" : "inherit",
                          }}
                        >
                          <SoftTypography
                            variant={isMobile ? "caption" : "subtitle2"}
                            fontWeight="medium"
                          >
                            {t("Qté")}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox
                          component="th"
                          py={isMobile ? 0.5 : 1}
                          textAlign="center"
                          sx={{
                            borderBottom: "2px solid black",
                            borderRight:
                              i18n.language === "ar" ? 0 : "2px solid black",
                            borderLeft:
                              i18n.language === "ar" ? "2px solid black" : 0,
                            width: isMobile ? "20%" : "auto",
                            fontSize: isMobile ? "0.7rem" : "inherit",
                          }}
                        >
                          <SoftTypography
                            variant={isMobile ? "caption" : "subtitle2"}
                            fontWeight="medium"
                          >
                            {t("Prix unitaire")}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox
                          component="th"
                          py={isMobile ? 0.5 : 1}
                          textAlign="center"
                          sx={{
                            borderBottom: "2px solid black",
                            width: isMobile ? "20%" : "auto",
                            fontSize: isMobile ? "0.7rem" : "inherit",
                          }}
                        >
                          <SoftTypography
                            variant={isMobile ? "caption" : "subtitle2"}
                            fontWeight="medium"
                          >
                            {t("Prix total")}
                          </SoftTypography>
                        </SoftBox>
                      </TableRow>
                    </SoftBox>

                    <TableBody sx={{ height: "100%" }}>
                      {/* Map through invoice details to display data rows */}
                      {invoiceDetails?.orders?.flatMap((order) =>
                        order?.order_lines?.map((line, lineIndex) => (
                          <TableRow key={`${order.id}-${lineIndex}`}>
                            <SoftBox component="td" textAlign="center">
                              <SoftTypography
                                variant={isMobile ? "caption" : "subtitle2"}
                              >
                                {line?.product_id}
                              </SoftTypography>
                            </SoftBox>
                            <SoftBox
                              component="td"
                              textAlign={
                                i18n.language === "ar" ? "right" : "left"
                              }
                              px={isMobile ? 0.5 : 1}
                              borderLeft="2px solid black"
                              borderRight="2px solid black"
                              sx={{ wordBreak: "break-word" }} // Handle long text
                            >
                              <SoftTypography
                                variant={isMobile ? "caption" : "subtitle2"}
                              >
                                {line?.product_name}
                              </SoftTypography>
                            </SoftBox>
                            <SoftBox
                              component="td"
                              textAlign="center"
                              px={isMobile ? 1 : 2}
                              borderLeft={
                                i18n.language === "ar" ? "2px solid black" : 0
                              }
                              borderRight={
                                i18n.language === "ar" ? 0 : "2px solid black"
                              }
                            >
                              <SoftTypography
                                variant={isMobile ? "caption" : "subtitle2"}
                              >
                                {line?.quantity || 0}
                              </SoftTypography>
                            </SoftBox>
                            <SoftBox
                              component="td"
                              textAlign="center"
                              borderLeft={
                                i18n.language === "ar" ? "2px solid black" : 0
                              }
                              borderRight={
                                i18n.language === "ar" ? 0 : "2px solid black"
                              }
                            >
                              <SoftTypography
                                variant={isMobile ? "caption" : "subtitle2"}
                              >
                                {line?.unit_price
                                  ? Number(line?.unit_price).toFixed(3)
                                  : "0.00"}
                              </SoftTypography>
                            </SoftBox>
                            <SoftBox component="td" textAlign="center">
                              <SoftTypography
                                variant={isMobile ? "caption" : "subtitle2"}
                              >
                                {line?.total_price
                                  ? Number(line?.total_price).toFixed(3)
                                  : "0.00"}
                              </SoftTypography>
                            </SoftBox>
                          </TableRow>
                        ))
                      )}

                      {/* Generate empty rows to fill the remaining table space */}
                      {[...Array(5)].map((_, index) => (
                        <TableRow key={`empty-${index}`}>
                          <SoftBox component="td" textAlign="center">
                            &nbsp;
                          </SoftBox>
                          <SoftBox
                            component="td"
                            textAlign={
                              i18n.language === "ar" ? "right" : "left"
                            }
                            px={isMobile ? 0.5 : 1}
                            sx={{
                              borderLeft: "2px solid black",
                              borderRight: "2px solid black",
                            }}
                          >
                            &nbsp;
                          </SoftBox>
                          <SoftBox
                            component="td"
                            textAlign="center"
                            px={isMobile ? 1 : 2}
                            sx={{
                              borderRight:
                                i18n.language === "ar" ? 0 : "2px solid black",
                              borderLeft:
                                i18n.language === "ar" ? "2px solid black" : 0,
                            }}
                          >
                            &nbsp;
                          </SoftBox>
                          <SoftBox
                            component="td"
                            textAlign="center"
                            sx={{
                              borderRight:
                                i18n.language === "ar" ? 0 : "2px solid black",
                              borderLeft:
                                i18n.language === "ar" ? "2px solid black" : 0,
                            }}
                          >
                            &nbsp;
                          </SoftBox>
                          <SoftBox component="td" textAlign="center">
                            &nbsp;
                          </SoftBox>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </SoftBox>

              <SoftBox>
                <Typography
                  variant={isMobile ? "caption" : "subtitle2"}
                  px={isMobile ? 2 : 3}
                  pt={3}
                  pb={1}
                >
                  {t("Timbre fiscal, Article 124, Loi 53/93")}: 1.000
                </Typography>

                <Grid
                  container
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  px={isMobile ? 2 : 3}
                  spacing={isMobile ? 2 : 0}
                >
                  <Grid item xs={12} md={4.5}>
                    <SoftBox
                      sx={{
                        border: "2px solid black",
                        borderRadius: 4,
                        overflow: "hidden",
                        mb: isMobile ? 2 : 0,
                      }}
                    >
                      <Table sx={{ borderCollapse: "collapse" }}>
                        <TableBody>
                          <TableRow>
                            <SoftBox
                              component="td"
                              textAlign={
                                i18n.language === "ar" ? "right" : "left"
                              }
                              px={isMobile ? 1 : 1.5}
                              pt={isMobile ? 1 : 1.5}
                              pb={1}
                            >
                              <SoftTypography
                                variant={isMobile ? "caption" : "subtitle2"}
                              >
                                {t("Total HT")}
                              </SoftTypography>
                            </SoftBox>
                            <SoftBox
                              component="td"
                              textAlign={
                                i18n.language === "ar" ? "right" : "left"
                              }
                              px={isMobile ? 1 : 1.5}
                              pt={isMobile ? 1 : 1.5}
                              pb={1}
                              borderRight={
                                i18n.language === "ar" ? "2px solid black" : 0
                              }
                              borderLeft={
                                i18n.language === "ar" ? 0 : "2px solid black"
                              }
                            >
                              <SoftTypography
                                variant={isMobile ? "caption" : "subtitle2"}
                              >
                                {invoiceDetails?.orders
                                  .reduce((acc, order) => {
                                    return acc + Number(order?.total_price);
                                  }, 0)
                                  .toFixed(3)}
                              </SoftTypography>
                            </SoftBox>
                          </TableRow>
                          <TableRow>
                            <SoftBox
                              component="td"
                              textAlign={
                                i18n.language === "ar" ? "right" : "left"
                              }
                              px={isMobile ? 1 : 1.5}
                            >
                              <SoftTypography
                                variant={isMobile ? "caption" : "subtitle2"}
                              >
                                {t("Total TTC")}
                              </SoftTypography>
                            </SoftBox>
                            <SoftBox
                              component="td"
                              textAlign={
                                i18n.language === "ar" ? "right" : "left"
                              }
                              px={isMobile ? 1 : 1.5}
                              pt={1}
                              borderRight={
                                i18n.language === "ar" ? "2px solid black" : 0
                              }
                              borderLeft={
                                i18n.language === "ar" ? 0 : "2px solid black"
                              }
                            >
                              <SoftTypography
                                variant={isMobile ? "caption" : "subtitle2"}
                              >
                                {(
                                  invoiceDetails?.orders.reduce(
                                    (acc, order) => {
                                      return acc + Number(order?.total_price);
                                    },
                                    0
                                  ) + Number(companyInfos?.stamp_value)
                                ).toFixed(3)}
                              </SoftTypography>
                            </SoftBox>
                          </TableRow>
                          <TableRow>
                            <SoftBox
                              component="td"
                              textAlign={
                                i18n.language === "ar" ? "right" : "left"
                              }
                              px={isMobile ? 1 : 1.5}
                              pb={isMobile ? 1 : 1.5}
                              pt={1}
                            >
                              <SoftTypography
                                variant={isMobile ? "caption" : "subtitle2"}
                              >
                                {t("Acompte fiscal")}
                              </SoftTypography>
                            </SoftBox>
                            <SoftBox
                              component="td"
                              textAlign={
                                i18n.language === "ar" ? "right" : "left"
                              }
                              px={isMobile ? 1 : 1.5}
                              pb={1}
                              pt={1}
                              borderRight={
                                i18n.language === "ar" ? "2px solid black" : 0
                              }
                              borderLeft={
                                i18n.language === "ar" ? 0 : "2px solid black"
                              }
                            >
                              <SoftTypography
                                variant={isMobile ? "caption" : "subtitle2"}
                              >
                                0.000
                              </SoftTypography>
                            </SoftBox>
                          </TableRow>
                          <TableRow>
                            <SoftBox
                              component="td"
                              textAlign={
                                i18n.language === "ar" ? "right" : "left"
                              }
                              px={isMobile ? 1 : 1.5}
                              py={isMobile ? 0.5 : 1}
                              borderTop="2px solid black"
                            >
                              <SoftTypography
                                variant={isMobile ? "caption" : "subtitle2"}
                              >
                                {t("Montant net à payer")}
                              </SoftTypography>
                            </SoftBox>
                            <SoftBox
                              component="td"
                              textAlign={
                                i18n.language === "ar" ? "right" : "left"
                              }
                              px={isMobile ? 1 : 1.5}
                              py={isMobile ? 1 : 1.5}
                              borderRight={
                                i18n.language === "ar" ? "2px solid black" : 0
                              }
                              borderLeft={
                                i18n.language === "ar" ? 0 : "2px solid black"
                              }
                              borderTop="2px solid black"
                            >
                              <SoftTypography
                                variant={isMobile ? "caption" : "subtitle2"}
                              >
                                {(
                                  invoiceDetails?.orders.reduce(
                                    (acc, order) => {
                                      return acc + Number(order?.total_price);
                                    },
                                    0
                                  ) + Number(companyInfos?.stamp_value)
                                ).toFixed(3)}
                              </SoftTypography>
                            </SoftBox>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </SoftBox>
                  </Grid>

                  <Grid item xs={12} py={isMobile ? 1 : 3}>
                    <SoftBox
                      p={isMobile ? 2 : 3}
                      sx={{
                        border: "2px solid black",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        fontSize={isMobile ? 13 : 15}
                        fontWeight="medium"
                      >
                        {t("Montant net en toutes lettres")}:
                        {i18n.language === "fr"
                          ? numberToWords(
                              (
                                invoiceDetails?.orders.reduce((acc, order) => {
                                  return acc + Number(order?.total_price);
                                }, 0) + Number(companyInfos?.stamp_value)
                              ).toFixed(3)
                            )
                          : i18n.language === "en"
                          ? numberToWordsEnglish(
                              (
                                invoiceDetails?.orders.reduce((acc, order) => {
                                  return acc + Number(order?.total_price);
                                }, 0) + Number(companyInfos?.stamp_value)
                              ).toFixed(3)
                            )
                          : numberToWordsArabic(
                              (
                                invoiceDetails?.orders.reduce((acc, order) => {
                                  return acc + Number(order?.total_price);
                                }, 0) + Number(companyInfos?.stamp_value)
                              ).toFixed(3)
                            )}
                      </Typography>
                    </SoftBox>
                  </Grid>
                </Grid>
              </SoftBox>
            </Card>
            <SoftBox
              width="100%"
              p={isMobile ? 2 : 3}
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Grid
                container
                spacing={2}
                justifyContent="flex-end"
                alignItems="center"
              >
                <Grid item xs={12} sm="auto">
                  <SoftBox
                    width="100%"
                    display="flex"
                    flexDirection={isMobile ? "column" : "row"}
                    alignItems="center"
                  >
                    {/* Arabic Download Button */}
                    {/* <SoftButton
                      variant="gradient"
                      color="info"
                      onClick={() => {
                        i18n.changeLanguage("ar");
                        downloadInvoice(invoiceDetails?.number);
                      }}
                      sx={{
                        marginBottom: isMobile ? 2 : 0,
                        marginRight: isMobile ? 0 : 2,
                        width: isMobile ? "100%" : "auto",
                      }}
                    >
                      {t("Télécharger la facture en arabe")}
                    </SoftButton> */}

                    {/* Regular Download Button */}
                    <SoftButton
                      variant="gradient"
                      color="info"
                      onClick={() => {
                        downloadInvoice(invoiceDetails?.number);
                      }}
                      sx={{
                        width: isMobile ? "100%" : "auto",
                      }}
                    >
                      {t("Télécharger")}
                    </SoftButton>
                  </SoftBox>
                </Grid>
              </Grid>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default InvoiceDetails;
